import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { ArrowLeft, Plus, Edit, Trash2, CheckCircle, XCircle, List } from 'lucide-react';

const ZhantiTypeManagement = ({ onBack }) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingType, setEditingType] = useState(null);

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const data = await apiService.getAdminZhantiTypes();
      setTypes(data);
    } catch (error) {
      console.error("Failed to fetch types", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    data.sort_order = parseInt(data.sort_order) || 0;
    data.is_active = data.is_active === 'on';

    try {
      if (editingType?.id) {
        await apiService.updateAdminZhantiType(editingType.id, data);
      } else {
        await apiService.createAdminZhantiType(data);
      }
      setEditingType(null);
      fetchTypes();
    } catch (error) {
      alert('保存失败: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除吗？')) return;
    try {
      await apiService.deleteAdminZhantiType(id);
      fetchTypes();
    } catch (error) {
      console.error("Failed to delete", error);
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md overflow-hidden min-h-[500px]">
      <div className="p-4 border-b flex items-center justify-between bg-gray-50">
        <div className="flex items-center">
          <button onClick={onBack} className="mr-4 text-gray-600 hover:text-indigo-600 flex items-center">
            <ArrowLeft className="h-5 w-5 mr-1" /> 返回
          </button>
          <h2 className="text-xl font-semibold text-gray-800">占题类型管理</h2>
        </div>
        <button
          onClick={() => setEditingType({})}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm flex items-center"
        >
          <Plus className="h-4 w-4 mr-1" /> 新增类型
        </button>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="text-center py-10">加载中...</div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">排序</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">名称</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">描述</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {types.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.sort_order}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{t.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.description || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {t.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle size={12} className="mr-1" /> 启用
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <XCircle size={12} className="mr-1" /> 禁用
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button onClick={() => setEditingType(t)} className="text-indigo-600 hover:text-indigo-900 mr-4">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => handleDelete(t.id)} className="text-red-600 hover:text-red-900">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Edit Modal */}
        {editingType && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-bold">{editingType.id ? '编辑类型' : '新增类型'}</h3>
                <button onClick={() => setEditingType(null)}><XCircle size={24} className="text-gray-400" /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">名称</label>
                  <input name="name" defaultValue={editingType.name} required className="w-full border rounded-lg p-2" placeholder="例如：求财" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
                  <input name="description" defaultValue={editingType.description} className="w-full border rounded-lg p-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">排序 (越小越靠前)</label>
                  <input name="sort_order" type="number" defaultValue={editingType.sort_order || 0} className="w-full border rounded-lg p-2" />
                </div>
                <div className="flex items-center">
                  <input id="is_active" name="is_active" type="checkbox" defaultChecked={editingType.is_active !== false} className="h-4 w-4 text-indigo-600 rounded" />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">启用该类型</label>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button type="button" onClick={() => setEditingType(null)} className="px-4 py-2 border rounded-lg">取消</button>
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg">保存</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZhantiTypeManagement;